/**
 * DirectoryBand — the forest personality band for the directory detail
 * surfaces. Both detail pages compose it: a forest gradient inside a
 * rounded-card with shadow-sh2, the dove watermark whispering in cream
 * at the top-right, the serif
 * display title in cream, one purpose line, and a meta line (the
 * recommended badge, then the category dot, then "Updated …"). The
 * identity slot (org tile / person icon tile) and the on-dark action
 * ladder are built by the composer and passed in.
 *
 * There is no visible eyebrow. The kind ("Organization" / "Contact")
 * lives sr-only, as the h1's first child, ahead of the name, so a
 * reader's accessible name is "Organization, <name>" (or "Contact, ").
 * The identity tile sits in its own box exactly one h1 line tall, so its
 * centre lands on the name's first line even when the name wraps.
 *
 * Band anatomy is design-language §3: personality lives in the band, gold
 * is a non-interactive accent here (never a fill), one primary per band.
 *
 * The title hosts the inline-editable name (nameSlot) for direct-write
 * roles only; the page wraps it so the button, its hover, its focus ring and
 * its saving note read on the band. Everyone else gets the band's own name,
 * in the h1's cream at its display size, wrapping rather than truncating.
 *
 * A nested interactive control's OWN aria-label is not reachable by a
 * screen reader's ordinary "name from content" walk of an ancestor (the
 * accname algorithm reads a nested control's visible text there, not its
 * label), only a DIRECT id reference resolves it. So when nameSlot is
 * an editable control, the composer also passes `nameSlotId` (the id it
 * gave that control, e.g. via EditableField's `id` prop), and the h1
 * points `aria-labelledby` straight at the kind span plus that id. A
 * reader's h1 (no nameSlot) needs no such wiring; its plain name span
 * already speaks through ordinary content.
 */

import { useId, type ReactNode } from 'react';
import { Building2, UserRound } from 'lucide-react';
import { HubBrandMark } from '@/components/hub/HubBrandMark';

// Forest gradient — tokens only (forest-900 is not a token; the deep end
// lands on sage-900, the deepest forest-family value in the scale).
const BAND_GRADIENT =
  'linear-gradient(155deg, var(--color-sage-800) 0%, var(--color-forest-700) 68%, var(--color-sage-900) 100%)';

// The h1's font-size/leading, mirrored on the identity tile's wrapping
// box so that box is exactly one h1 line tall, the tile centres on the
// name's FIRST line regardless of how many lines the name wraps to.
const H1_FONT_SIZE = 'clamp(26px,3vw,33px)';
const H1_LINE_HEIGHT = 1.1;

const KIND_PREFIX: Record<'organization' | 'person', string> = {
  organization: 'Organization, ',
  person: 'Contact, ',
};

export interface DirectoryBandProps {
  /** Sr-only "Organization, " / "Contact, " at the start of the h1. */
  kind: 'organization' | 'person';
  /** The identity tile (see BandOrgIdentity / BandPersonIdentity). */
  identity: ReactNode;
  /** a11y + read-only fallback name. */
  name: string;
  /** Editable name (inherits cream + display step). */
  nameSlot?: ReactNode;
  /** The id the composer gave nameSlot's own control (e.g. EditableField's
   *  `id`), so the h1's accessible name can reference it directly. */
  nameSlotId?: string;
  /** ONE purpose line. */
  purpose?: string;
  /**
   * An interactive line under the title (cream/85) — takes precedence over
   * `purpose`. Used for the individual's inline-editable title.
   */
  subline?: ReactNode;
  /** Category name, rendered after a small gold dot in the meta line. */
  categoryLabel?: string;
  /** e.g. "Updated 3 days ago". */
  updatedLabel?: ReactNode;
  /** A small gold tag under the title (the referral-partner tag). */
  tag?: ReactNode;
  /** The recommended badge, first item on the meta line when present. */
  badge?: ReactNode;
  /** The on-dark action ladder (one primary). */
  actions?: ReactNode;
  testId?: string;
}

export function DirectoryBand({
  kind,
  identity,
  name,
  nameSlot,
  nameSlotId,
  purpose,
  subline,
  categoryLabel,
  updatedLabel,
  tag,
  badge,
  actions,
  testId,
}: DirectoryBandProps): ReactNode {
  const hasMeta = Boolean(badge || categoryLabel || updatedLabel);
  const kindId = useId();
  return (
    <div
      data-testid={testId ?? 'directory-band'}
      className="relative overflow-hidden rounded-card text-cream shadow-sh2"
      style={{ backgroundImage: BAND_GRADIENT }}
    >
      {/* Dove watermark — cream whisper, top-right. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-6 -top-8 w-[180px] text-cream/[6%] sm:w-[220px]"
      >
        <HubBrandMark label="" className="flex [&>svg]:h-auto [&>svg]:w-full" />
      </div>

      <div className="relative flex flex-col gap-5 p-6 sm:flex-row sm:items-start sm:justify-between sm:gap-6 sm:p-7">
        <div className="flex min-w-0 items-center gap-4">
          {/* One h1 line tall, items-center, the identity tile's centre
              sits on the name's first line. `self-start` keeps its top
              pinned to the row's top (the h1's top) independent of the
              row's own items-center default, so a taller block below
              (subline, tag, meta line) never pulls the tile down. */}
          <div
            className="flex shrink-0 items-center self-start"
            style={{ height: `calc(${H1_LINE_HEIGHT} * ${H1_FONT_SIZE})` }}
          >
            {identity}
          </div>
          <div className="min-w-0">
            <h1
              className="flex min-w-0 flex-wrap items-center gap-2 font-display text-[clamp(26px,3vw,33px)] font-semibold leading-[1.1] tracking-[-0.01em] text-cream"
              aria-labelledby={nameSlot && nameSlotId ? `${kindId} ${nameSlotId}` : undefined}
            >
              <span id={kindId} className="sr-only">{KIND_PREFIX[kind]}</span>
              {nameSlot ?? (
                <span className="break-words" title={name}>
                  {name}
                </span>
              )}
            </h1>
            {tag && <div className="mt-2.5">{tag}</div>}
            {subline ? (
              <div className="mt-2.5 text-[14.5px] leading-snug text-cream/85">{subline}</div>
            ) : purpose ? (
              <p className="mt-2.5 max-w-xl text-[14.5px] leading-snug text-cream/85">{purpose}</p>
            ) : null}
            {hasMeta && (
              <div
                data-testid="directory-band-meta"
                className="mt-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12.5px] text-cream/70"
              >
                {badge}
                {badge && (categoryLabel || updatedLabel) && (
                  <span aria-hidden="true"> · </span>
                )}
                {categoryLabel && (
                  <span className="inline-flex items-center gap-1.5">
                    <span aria-hidden="true" className="h-1.5 w-1.5 rounded-pill bg-warm-400" />
                    {categoryLabel}
                  </span>
                )}
                {categoryLabel && updatedLabel && <span aria-hidden="true"> · </span>}
                {updatedLabel && <span>{updatedLabel}</span>}
              </div>
            )}
          </div>
        </div>

        {actions && (
          <div
            data-testid="directory-band-actions"
            className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end sm:pt-1"
          >
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

/** Shared 46px cream-tinted rounded-square tile with a gold glyph. Both
 *  BandOrgIdentity and BandPersonIdentity render through this one
 *  internal component, so their class strings byte-match. */
function BandIdentityTile({ icon }: { icon: ReactNode }): ReactNode {
  return (
    <span
      aria-hidden="true"
      className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-base border border-cream/[18%] bg-cream/10 text-warm-400"
    >
      {icon}
    </span>
  );
}

/** The org identity tile, a building glyph in gold. */
export function BandOrgIdentity(): ReactNode {
  return <BandIdentityTile icon={<Building2 size={22} strokeWidth={1.7} />} />;
}

/** The person identity tile, a person glyph in gold (no initials; no name prop). */
export function BandPersonIdentity(): ReactNode {
  return <BandIdentityTile icon={<UserRound size={22} strokeWidth={1.7} />} />;
}
